import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

/**
 * AutocompleteInput
 *
 * Wraps a normal text input with a dropdown showing previously-typed values
 * for the given `field` (stored server-side in input_history). When the user
 * blurs the input with a non-empty value, the value is upserted into history.
 *
 * Props:
 *  - field: string — namespacing key, e.g. "sj.material_name", "po.client_name"
 *  - value: current value (string)
 *  - onChange: (value) => void
 *  - placeholder, className, ...rest: passed to underlying <input>
 *  - dataTestid: optional test ID
 *  - persistOnBlur: default true; set false to disable autosave (use explicit api call instead)
 */
export function AutocompleteInput({
  field,
  value,
  onChange,
  placeholder,
  className = "",
  dataTestid,
  persistOnBlur = true,
  limit = 15,
  type = "text",
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Iter v34cf — touched=true setelah user mengetik. Sebelum diketik, jangan
  // pakai nilai pre-filled untuk memfilter dropdown (agar semua history tampil).
  const [touched, setTouched] = useState(false);
  const containerRef = useRef(null);

  const load = async (q) => {
    if (!field) return;
    try {
      const r = await api.get("/input-history", { params: { field, q: q != null ? String(q) : "" } });
      setOptions(r.data || []);
      setLoaded(true);
    } catch {
      setOptions([]);
    }
  };

  const onFocus = () => {
    setOpen(true);
    // Iter v34cf — always fetch fresh (empty q) so pre-filled defaults still show history.
    load("");
  };

  const onBlur = async () => {
    // Delay so click on a dropdown option fires before closing
    setTimeout(() => setOpen(false), 150);
    if (persistOnBlur && value != null && String(value).trim()) {
      try { await api.post("/input-history", { field, value: String(value).trim() }); } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Iter v34cf — coerce value to string for filter to avoid crash when caller passes a number.
  // Sebelum user mengetik (touched=false), tampilkan seluruh options tanpa filter substring.
  const _valStr = value == null ? "" : String(value);
  const filtered = (!touched || !_valStr)
    ? options
    : options.filter((o) => String(o.value || "").toLowerCase().includes(_valStr.toLowerCase()));

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type={type}
        value={value == null ? "" : value}
        onChange={(e) => { setTouched(true); onChange(e.target.value); if (loaded) setOpen(true); }}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-terra-500 disabled:bg-stone-100 disabled:text-stone-400 disabled:cursor-not-allowed ${className}`}
        data-testid={dataTestid}
        {...rest}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-30 mt-0.5 left-0 right-0 bg-white border border-stone-200 shadow-md max-h-56 overflow-y-auto" data-testid={dataTestid ? `${dataTestid}-dropdown` : undefined}>
          {filtered.slice(0, limit).map((o, i) => (
            <button
              key={o.id || i}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setOpen(false); }}
              className="block w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50 border-b border-stone-100 last:border-b-0"
              data-testid={dataTestid ? `${dataTestid}-opt-${i}` : undefined}
            >
              <span className="font-medium text-stone-800">{o.value}</span>
              {o.use_count > 1 && <span className="ml-2 text-[10px] text-stone-400 font-mono">×{o.use_count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
